import { checkNullData } from 'utils';
import { cloneDeep } from 'lodash/fp';
import { adaptPlotsChart } from './adaptPlotsChart';
import { CHART_DISPLAY_OPTION } from '../pages/ClientOverview/pages/Portfolios/pages/SinglePortfolioView/constants';

export const adaptPlot = (data, useAmountPerformance = false, key = CHART_DISPLAY_OPTION.percentage) => {
    const adaptValue = (item, { valueToDivide, firstValue }) => {
        if (key === CHART_DISPLAY_OPTION.percentage) {
            const divider = valueToDivide || (useAmountPerformance && firstValue);
            const value = divider ? item.Value / divider.Value - 1 : item.Value;

            return Math.round(value * 10000) / 100;
        }

        return item.Amount;
    };

    return {
        data: adaptPlotsChart({
            data: data?.HistoricalPerformance?.[0]?.Values || [],
            chartStartDate: data?.startDate,
            chartEndDate: data?.endDate,
            adaptValue,
        }),
        title: data?.title,
    };
};

const adaptRiskReturn = (portfolio, strategy, {
    benchmark, t, portfolioTitle, strategyTitle,
}) => {
    const data: any[] = [];

    if (!checkNullData(portfolio)) {
        data[0] = {
            name: portfolioTitle || t('clientDashboard.portfolio.currentPortfolio'),
            data: [portfolio],
        };
    }

    if (!checkNullData(strategy)) {
        data[1] = {
            name: strategyTitle || t('clientDashboard.portfolio.newPortfolio'),
            data: [strategy],
        };
    }

    if (!checkNullData(benchmark)) return [...data, cloneDeep(benchmark)];

    return data;
};

const getOptionalRiskReturn = (portfolio, strategy, benchmark, tooltipFormat, t) => {
    if (checkNullData(portfolio)) {
        return {
            xLabels: [
                t('clientDashboard.portfolio.low'),
                t('clientDashboard.portfolio.expectedRisk'),
                t('clientDashboard.portfolio.high'),
            ],
            yLabels: [
                t('clientDashboard.portfolio.low'),
                t('clientDashboard.portfolio.expectedReturn'),
                t('clientDashboard.portfolio.high'),
            ],
            tooltipFormat,
        };
    }

    const middleX = portfolio?.[0] || 0;
    const middleY = portfolio?.[1] || 0;
    let maxX = Math.max(
        strategy ? strategy[0] + 1 : 0,
        benchmark ? benchmark.data?.[0]?.[0] + 1 : 0,
        middleX * 2,
    );
    let maxY = Math.max(
        strategy ? strategy[1] + 1 : 0,
        benchmark ? benchmark.data?.[0]?.[1] + 1 : 0,
        middleY * 2,
    );
    let minX = Math.min(
        strategy ? strategy[0] - 1 : 0,
        benchmark ? benchmark.data?.[0]?.[0] - 1 : 0,
        0,
    );
    let minY = Math.min(
        strategy ? strategy[1] - 1 : 0,
        benchmark ? benchmark.data?.[0]?.[1] - 1 : 0,
        0,
    );

    if (minX < 0) maxX = middleX + (middleX - minX);
    else minX = middleX * 2 - maxX;

    if (minY < 0) maxY = middleY + (middleY - minY);
    else minY = middleY * 2 - maxY;

    return {
        minX,
        minY,
        maxX,
        maxY,
        xLabels: [
            t('clientDashboard.portfolio.low'),
            t('clientDashboard.portfolio.expectedRisk'),
            t('clientDashboard.portfolio.high'),
        ],
        yLabels: [
            t('clientDashboard.portfolio.low'),
            t('clientDashboard.portfolio.expectedReturn'),
            t('clientDashboard.portfolio.high'),
        ],
        tooltipFormat,
    };
};

export const adaptRiskCompare = (portfolio, strategy, {
    tooltipFormat,
    t,
    getFormattedNumber,
    benchmark,
    strategyTitle,
    matrix,
}) => {
    const riskReturnStrategy = strategy?.Volatility && strategy?.ExpectedReturn
        ? [+(strategy.Volatility * 100), +(strategy.ExpectedReturn * 100)]
        : null;
    const optional = getOptionalRiskReturn(portfolio?.riskReturn, riskReturnStrategy, benchmark, tooltipFormat, t);
    const minMatrixX = getFormattedNumber(matrix?.x[0] || 0);
    const maxMatrixX = getFormattedNumber(Math.min(matrix?.x[1] || 0, optional?.maxX as number));

    return {
        title: t('clientDashboard.portfolio.riskReturn'),
        data: adaptRiskReturn(portfolio?.riskReturn, riskReturnStrategy, {
            benchmark,
            t,
            portfolioTitle: portfolio?.riskReturnTitle,
            strategyTitle,
        }),
        optional: {
            ...optional,
            matrix: matrix?.x ? {
                ...matrix,
                name: (minMatrixX !== 'NaN' && maxMatrixX !== 'NaN')
                    && `${t('clientDashboard.portfolio.riskBandwidth')} (${minMatrixX}% - ${maxMatrixX}%)`,
            } : undefined,
        },
    };
};
